import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle, User, Users, GraduationCap, Loader2 } from 'lucide-react';

const EMPTY_FORM = {
  nama_lengkap: '',
  jenis_kelamin: '',
  tempat_lahir: '',
  tanggal_lahir: '',
  asal_sekolah: '',
  kelas: '',
  alamat: '',
  telepon: '',
  email: '',
  nama_orang_tua: '',
  telepon_orang_tua: '',
  alamat_orang_tua: '',
  program: '',
  mata_pelajaran: [],
  hari: '',
  waktu: '',
  persetujuan: false,
  tanggal_daftar: new Date().toISOString().split('T')[0],
};

const KELAS_OPTIONS = [
  'SD Kelas 1', 'SD Kelas 2', 'SD Kelas 3',
  'SD Kelas 4', 'SD Kelas 5', 'SD Kelas 6',
  'SMP Kelas 7', 'SMP Kelas 8', 'SMP Kelas 9',
  'SMA Kelas 10', 'SMA Kelas 11', 'SMA Kelas 12',
];

const MATA_PELAJARAN = [
  'Matematika', 'IPA', 'IPS',
  'Bahasa Indonesia', 'Bahasa Inggris', 'PKN',
];

const HARI = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

const PendaftaranPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMapelChange = (mapel) => {
    setFormData(prev => {
      const cur = prev.mata_pelajaran;
      return {
        ...prev,
        mata_pelajaran: cur.includes(mapel)
          ? cur.filter(m => m !== mapel)
          : [...cur, mapel],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const MAX_SUBMISSIONS = 2;
    const submissionCount = parseInt(localStorage.getItem('submissionCount') || '0', 10);

    if (submissionCount >= MAX_SUBMISSIONS) {
      toast.error('Batas Pendaftaran Tercapai', {
        description: 'Anda hanya dapat melakukan pendaftaran maksimal 2 kali.',
      });
      return;
    }

    if (!formData.persetujuan) {
      toast.error('Persetujuan Diperlukan', {
        description: 'Centang kotak persetujuan sebelum mengirim.',
      });
      return;
    }

    if (formData.mata_pelajaran.length === 0) {
      toast.error('Mata Pelajaran Diperlukan', {
        description: 'Pilih minimal 1 mata pelajaran.',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('https://apikasi-bimbel-fix.vercel.app/api/pendaftaran', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        localStorage.setItem('submissionCount', (submissionCount + 1).toString());
        setSubmitted(true);
        toast.success('Pendaftaran Berhasil!', {
          description: 'Data Anda telah kami terima dan akan segera diproses.',
        });
      } else {
        throw new Error(result.error || 'Gagal mengirim data pendaftaran.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Pendaftaran Gagal', {
        description: error.message || 'Cek koneksi internet atau hubungi admin.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── Sukses ── */
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-[#5A9C9B]/10 via-[#f2f5fa] to-[#f26d0f]/10">
        <div className="w-full max-w-sm mx-auto text-center py-16 space-y-5">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Pendaftaran Berhasil!</h2>
          <p className="text-gray-500 text-sm leading-relaxed">
            Data Anda telah kami terima. Tim kami akan segera menghubungi Anda untuk konfirmasi lebih lanjut.
          </p>
          <button
            onClick={() => { setSubmitted(false); setFormData(EMPTY_FORM); }}
            className="text-sm text-[#5A9C9B] underline"
          >
            Daftar lagi
          </button>
        </div>
      </div>
    );
  }

  /* ── Form ── */
  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <section className="pt-24 pb-10 px-4 bg-gradient-to-br from-[#5A9C9B]/10 via-[#f2f5fa] to-[#f26d0f]/10">
        <div className="max-w-xl mx-auto text-center space-y-3">
          <span className="inline-block bg-[#f26d0f]/15 text-[#f26d0f] text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wide">
            Formulir Resmi
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
            Formulir Pendaftaran
          </h1>
          <p className="text-sm md:text-base text-gray-500">
            Isi dengan lengkap dan benar. Kolom bertanda <span className="text-red-500">*</span> wajib diisi.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="px-4 py-8 md:py-12">
        <form onSubmit={handleSubmit} className="max-w-xl mx-auto space-y-5">

          {/* ── Data Siswa ── */}
          <Card className="border-0 shadow-md rounded-2xl overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-[#5A9C9B] to-[#f26d0f]" />
            <CardContent className="p-5 md:p-7 space-y-4">

              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#5A9C9B] rounded-xl flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Data Siswa</h2>
              </div>

              {/* Nama Lengkap */}
              <div className="space-y-1.5">
                <Label htmlFor="nama_lengkap" className="text-sm font-medium">Nama Lengkap *</Label>
                <Input
                  id="nama_lengkap" name="nama_lengkap"
                  value={formData.nama_lengkap} onChange={handleInputChange}
                  placeholder="Nama sesuai akta kelahiran"
                  required className="h-12 rounded-xl text-base"
                />
              </div>

              {/* Jenis Kelamin & Tempat Lahir */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Jenis Kelamin *</Label>
                  <Select value={formData.jenis_kelamin} onValueChange={v => handleSelectChange('jenis_kelamin', v)}>
                    <SelectTrigger className="h-12 rounded-xl text-base">
                      <SelectValue placeholder="Pilih jenis kelamin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Laki-laki">Laki-laki</SelectItem>
                      <SelectItem value="Perempuan">Perempuan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tempat_lahir" className="text-sm font-medium">Tempat Lahir *</Label>
                  <Input
                    id="tempat_lahir" name="tempat_lahir"
                    value={formData.tempat_lahir} onChange={handleInputChange}
                    placeholder="Kota tempat lahir"
                    required className="h-12 rounded-xl text-base"
                  />
                </div>
              </div>

              {/* Tanggal Lahir & Kelas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="tanggal_lahir" className="text-sm font-medium">Tanggal Lahir *</Label>
                  <Input
                    id="tanggal_lahir" name="tanggal_lahir" type="date"
                    value={formData.tanggal_lahir} onChange={handleInputChange}
                    required className="h-12 rounded-xl text-base"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Kelas *</Label>
                  <Select value={formData.kelas} onValueChange={v => handleSelectChange('kelas', v)}>
                    <SelectTrigger className="h-12 rounded-xl text-base">
                      <SelectValue placeholder="Pilih kelas" />
                    </SelectTrigger>
                    <SelectContent>
                      {KELAS_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Asal Sekolah */}
              <div className="space-y-1.5">
                <Label htmlFor="asal_sekolah" className="text-sm font-medium">Asal Sekolah *</Label>
                <Input
                  id="asal_sekolah" name="asal_sekolah"
                  value={formData.asal_sekolah} onChange={handleInputChange}
                  placeholder="Nama sekolah saat ini"
                  required className="h-12 rounded-xl text-base"
                />
              </div>

              {/* Alamat */}
              <div className="space-y-1.5">
                <Label htmlFor="alamat" className="text-sm font-medium">Alamat Lengkap *</Label>
                <Textarea
                  id="alamat" name="alamat"
                  value={formData.alamat} onChange={handleInputChange}
                  placeholder="Jalan, RT/RW, Kelurahan, Kecamatan, Kota"
                  required rows={3} className="rounded-xl text-base resize-none"
                />
              </div>

              {/* Telepon & Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="telepon" className="text-sm font-medium">No. Telepon/WA Siswa *</Label>
                  <Input
                    id="telepon" name="telepon" type="tel"
                    value={formData.telepon} onChange={handleInputChange}
                    placeholder="08xxxxxxxxxx"
                    required className="h-12 rounded-xl text-base"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
                  <Input
                    id="email" name="email" type="email"
                    value={formData.email} onChange={handleInputChange}
                    placeholder="email@contoh.com"
                    required className="h-12 rounded-xl text-base"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Data Orang Tua ── */}
          <Card className="border-0 shadow-md rounded-2xl overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-[#5A9C9B] to-[#f26d0f]" />
            <CardContent className="p-5 md:p-7 space-y-4">

              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#5A9C9B] rounded-xl flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Data Orang Tua</h2>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="nama_orang_tua" className="text-sm font-medium">Nama Orang Tua (Ayah/Ibu) *</Label>
                <Input
                  id="nama_orang_tua" name="nama_orang_tua"
                  value={formData.nama_orang_tua} onChange={handleInputChange}
                  placeholder="Nama lengkap orang tua/wali"
                  required className="h-12 rounded-xl text-base"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="telepon_orang_tua" className="text-sm font-medium">No. HP Orang Tua *</Label>
                <Input
                  id="telepon_orang_tua" name="telepon_orang_tua" type="tel"
                  value={formData.telepon_orang_tua} onChange={handleInputChange}
                  placeholder="08xxxxxxxxxx"
                  required className="h-12 rounded-xl text-base"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="alamat_orang_tua" className="text-sm font-medium">Alamat Orang Tua *</Label>
                <Textarea
                  id="alamat_orang_tua" name="alamat_orang_tua"
                  value={formData.alamat_orang_tua} onChange={handleInputChange}
                  placeholder="Jika sama dengan siswa, isi ulang"
                  required rows={3} className="rounded-xl text-base resize-none"
                />
              </div>
            </CardContent>
          </Card>

          {/* ── Program Bimbel ── */}
          <Card className="border-0 shadow-md rounded-2xl overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-[#5A9C9B] to-[#f26d0f]" />
            <CardContent className="p-5 md:p-7 space-y-4">

              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#5A9C9B] rounded-xl flex items-center justify-center shrink-0">
                  <GraduationCap className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Program Bimbel</h2>
              </div>

              {/* Program */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Program yang Diambil *</Label>
                <Select value={formData.program} onValueChange={v => handleSelectChange('program', v)}>
                  <SelectTrigger className="h-12 rounded-xl text-base">
                    <SelectValue placeholder="Pilih program" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reguler">Reguler</SelectItem>
                    <SelectItem value="intensif">Intensif</SelectItem>
                    <SelectItem value="privat">Privat</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Mata Pelajaran */}
              <div className="space-y-2.5">
                <Label className="text-sm font-medium">
                  Mata Pelajaran *{' '}
                  <span className="text-gray-400 font-normal">(pilih minimal 1)</span>
                </Label>
                <div className="grid grid-cols-2 gap-2.5">
                  {MATA_PELAJARAN.map((mapel) => (
                    <label
                      key={mapel}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
                        formData.mata_pelajaran.includes(mapel)
                          ? 'border-[#5A9C9B] bg-[#5A9C9B]/8 text-[#5A9C9B]'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Checkbox
                        checked={formData.mata_pelajaran.includes(mapel)}
                        onCheckedChange={() => handleMapelChange(mapel)}
                        className="shrink-0"
                      />
                      <span className="text-sm font-medium">{mapel}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Hari & Waktu */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Jadwal Hari *</Label>
                  <Select value={formData.hari} onValueChange={v => handleSelectChange('hari', v)}>
                    <SelectTrigger className="h-12 rounded-xl text-base">
                      <SelectValue placeholder="Pilih hari" />
                    </SelectTrigger>
                    <SelectContent>
                      {HARI.map(h => (
                        <SelectItem key={h} value={h.toLowerCase()}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Jadwal Waktu *</Label>
                  <Select value={formData.waktu} onValueChange={v => handleSelectChange('waktu', v)}>
                    <SelectTrigger className="h-12 rounded-xl text-base">
                      <SelectValue placeholder="Pilih waktu" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sore">Sore (16.00–20.00)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Persetujuan & Submit ── */}
          <Card className="border-0 shadow-md rounded-2xl overflow-hidden">
            <CardContent className="p-5 md:p-7 space-y-5">

              {/* Persetujuan */}
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  id="persetujuan"
                  checked={formData.persetujuan}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, persetujuan: checked }))}
                  className="mt-0.5 shrink-0"
                />
                <div className="space-y-0.5">
                  <p className="text-sm font-medium text-gray-900 select-none leading-snug">
                    Saya menyetujui syarat dan ketentuan yang berlaku di Bimbel ini.
                  </p>
                  <p className="text-xs text-gray-500 select-none">
                    Data yang saya isikan adalah benar dan dapat dipertanggungjawabkan.
                  </p>
                </div>
              </label>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 text-base font-bold rounded-2xl bg-[#5A9C9B] hover:bg-[#4a8584] text-white shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Mengirim...</>
                ) : (
                  <><CheckCircle className="w-5 h-5 mr-2" /> Kirim Pendaftaran</>
                )}
              </Button>

              <p className="text-center text-xs text-gray-400">
                Butuh bantuan? Hubungi kami via WhatsApp
              </p>
            </CardContent>
          </Card>

        </form>
      </section>
    </div>
  );
};

export default PendaftaranPage;
